import { supabase } from "../../supabase/supabase";
import { ScheduledCall, ScheduledCallFormData } from "../models/ScheduledCall";
import { PendingScheduledCallsResponse } from "../types/pendingScheduledCalls";
import * as Sentry from "@sentry/react";

// Cache for API responses
const responseCache = new Map<string, { data: PendingScheduledCallsResponse; timestamp: number }>();
const CACHE_DURATION = 60000; // 1 minute cache

// Mock data for development/testing
const getMockPendingScheduledCalls = (): PendingScheduledCallsResponse => {
  Sentry.logger.debug('Creating mock data for scheduled calls', { 
    log_source: 'scheduled_call_service' 
  });
  const today = new Date().toISOString().split('T')[0];
  return {
    success: true,
    data: [
      {
        organization: {
          id: 2,
          name: "F22Labs"
        },
        pendingCalls: [
          {
            id: 1792,
            scheduled_time: "2025-07-22T06:00:00.000Z",
            scheduled_time_ist: "2025-07-22T11:30:00.000Z",
            call_type: "video",
            source: "public_link",
            profile: {
              id: 4711,
              name: "VISHAL SINGH",
              primary_phone: "+917086867731"
            },
            job: {
              id: 244,
              name: "Product Manager - 1-5 YOE",
              position: "Product Manager",
              company_name: "F22 Labs",
              recruitment_firm_name: "F22 Labs"
            }
          },
          {
            id: 1805,
            scheduled_time: "2025-07-22T06:30:00.000Z",
            scheduled_time_ist: "2025-07-22T12:00:00.000Z",
            source: "public_link",
            call_type: "video",
            profile: {
              id: 4737,
              name: "Paidy Nikhil",
              primary_phone: "+916302043012"
            },
            job: {
              id: 355,
              name: "Tilicho Front End Developer - React",
              position: "Front End Developer",
              company_name: "Tilicho",
              recruitment_firm_name: ""
            }
          }
        ],
        totalPendingCalls: 2
      },
      {
        organization: {
          id: 15,
          name: "Peoplefy"
        },
        pendingCalls: [
          {
            id: 1809,
            scheduled_time: "2025-07-22T06:00:00.000Z",
            scheduled_time_ist: "2025-07-22T11:30:00.000Z",
            source: "internal",
            call_type: "audio",
            profile: {
              id: 4742,
              name: "AKASH KUMARE",
              primary_phone: "+918149392137"
            },
            job: {
              id: 309,
              name: "TSYS_Jr Enterprise Architect",
              position: "Jr Enterprise Architect",
              company_name: "Peoplefy",
              recruitment_firm_name: "Peoplefy"
            }
          }
        ],
        totalPendingCalls: 1
      }
    ],
    totalOrganizations: 2,
    totalPendingCalls: 3,
    timeframe: {
      startDate: `${today} 00:00:00`,
      endDate: `${today} 23:59:59`
    }
  };
};

export const scheduledCallService = {
  async getScheduledCalls(organizationId: string): Promise<ScheduledCall[]> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .select("*")
      .order("scheduled_time", { ascending: true });

    if (error) {
      Sentry.logger.error('Error fetching scheduled calls', { 
        error,
        log_source: 'scheduled_call_service',
        organization_id: organizationId
      });
      throw error;
    }

    return data || [];
  },

  async getScheduledCallById(id: string): Promise<ScheduledCall | null> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      Sentry.logger.error('Error fetching scheduled call by ID', { 
        error,
        log_source: 'scheduled_call_service',
        call_id: id
      });
      throw error;
    }

    return data;
  },

  async getScheduledCallsForLead(leadId: string): Promise<ScheduledCall[]> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .select("*")
      .eq("lead_id", leadId)
      .eq("status", "scheduled")
      .order("scheduled_time", { ascending: true });

    if (error) {
      Sentry.logger.error('Error fetching scheduled calls for lead', { 
        error,
        log_source: 'scheduled_call_service',
        lead_id: leadId
      });
      throw error;
    }

    return data || [];
  },

  async getScheduledCallsForCampaign(campaignId: string): Promise<ScheduledCall[]> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .select("*")
      .eq("campaign_id", campaignId)
      .eq("status", "scheduled")
      .order("scheduled_time", { ascending: true });

    if (error) {
      Sentry.logger.error('Error fetching scheduled calls for campaign', { 
        error,
        log_source: 'scheduled_call_service',
        campaign_id: campaignId
      });
      throw error;
    }

    return data || [];
  },

  async createScheduledCall(
    formData: ScheduledCallFormData
  ): Promise<ScheduledCall> {
    // Insert the scheduled call directly
    const { data: scheduledCall, error: createError } = await supabase
      .from("scheduled_calls")
      .insert([
        {
          lead_id: formData.lead_id,
          assistant_id: formData.assistant_id,
          phone_number_id: formData.phone_number_id,
          campaign_id: formData.campaign_id,
          scheduled_time: formData.scheduled_time,
          custom_variables: formData.custom_variables || {},
          assistant_variables: formData.assistant_variables || {},
          status: "scheduled",
        },
      ])
      .select()
      .single();

    if (createError) {
      Sentry.logger.error('Error creating scheduled call', { 
        error: createError,
        log_source: 'scheduled_call_service',
        lead_id: formData.lead_id,
        assistant_id: formData.assistant_id
      });
      throw createError;
    }

    return scheduledCall;
  },

  async rescheduleCall(
    id: string,
    scheduledTime: string
  ): Promise<ScheduledCall> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .update({ scheduled_time: scheduledTime })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      Sentry.logger.error('Error rescheduling call', { 
        error,
        log_source: 'scheduled_call_service',
        call_id: id,
        new_scheduled_time: scheduledTime
      });
      throw error;
    }

    return data;
  },

  async cancelScheduledCall(id: string): Promise<ScheduledCall> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .update({ status: "cancelled" })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      Sentry.logger.error('Error cancelling scheduled call', { 
        error,
        log_source: 'scheduled_call_service',
        call_id: id
      });
      throw error;
    }

    return data;
  },

  async getAllScheduledCalls(): Promise<ScheduledCall[]> {
    const { data, error } = await supabase
      .from("scheduled_calls")
      .select("*")
      .eq("status", "scheduled")
      .order("scheduled_time", { ascending: true });
    // No need to filter by organization_id as RLS policies will handle this

    if (error) {
      Sentry.logger.error('Error fetching all scheduled calls', { 
        error,
        log_source: 'scheduled_call_service'
      });
      throw error;
    }

    return data || [];
  },


  async getPendingScheduledCalls(
    startDate?: string,
    endDate?: string
  ): Promise<PendingScheduledCallsResponse> {
    Sentry.logger.info('getPendingScheduledCalls called', { 
      log_source: 'scheduled_call_service',
      start_date: startDate,
      end_date: endDate
    });
    
    // Check cache first
    const cacheKey = `${startDate || 'default'}-${endDate || 'default'}`;
    const cached = responseCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      Sentry.logger.debug('Returning cached data', { 
        log_source: 'scheduled_call_service'
      });
      return cached.data;
    }
    
    // Development mode fallback - check if we should use mock data
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const mockFromStorage = localStorage.getItem('scheduledCalls_useMock') === 'true';
    const mockFromUrl = window.location.search.includes('mock=true');
    const useMockData = isDevelopment && (mockFromStorage || mockFromUrl);
    
    Sentry.logger.debug('Environment check for mock data', { 
      log_source: 'scheduled_call_service',
      is_development: isDevelopment,
      mock_from_storage: mockFromStorage,
      mock_from_url: mockFromUrl,
      use_mock_data: useMockData,
      hostname: window.location.hostname
    });
    
    if (useMockData) {
      Sentry.logger.info('Using mock data for scheduled calls (development mode)', { 
        log_source: 'scheduled_call_service'
      });
      // Add a small delay to simulate network request
      await new Promise(resolve => setTimeout(resolve, 500));
      Sentry.logger.debug('Returning mock data', { 
        log_source: 'scheduled_call_service'
      });
      return getMockPendingScheduledCalls();
    }

    // Default to today's date if no dates provided
    const today = new Date();
    const defaultStartDate = startDate || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')} 00:00:00`;
    const defaultEndDate = endDate || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')} 23:59:59`;

    const requestBody = {
      startDate: defaultStartDate,
      endDate: defaultEndDate
    };

    Sentry.logger.info('Making API request to scheduled calls endpoint', { 
      log_source: 'scheduled_call_service',
      endpoint: `${import.meta.env.VITE_HIREVOX_API_URL}/scheduled-calls/pending-scheduled-calls`,
      payload: requestBody
    });

    try {
      // Shorter timeout for faster feedback
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const startTime = Date.now();
      const response = await fetch(`${import.meta.env.VITE_HIREVOX_API_URL}/scheduled-calls/pending-scheduled-calls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_VOXI_API_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      Sentry.logger.info('API response received', { 
        log_source: 'scheduled_call_service',
        response_time_ms: responseTime,
        status: response.status,
        status_text: response.statusText
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        Sentry.logger.error('API error response', { 
          log_source: 'scheduled_call_service',
          error_text: errorText,
          status: response.status
        });
        
        // If API fails in development, offer mock data fallback
        if (isDevelopment) {
          Sentry.logger.warn('API failed in development, mock data available', { 
            log_source: 'scheduled_call_service',
            suggestion: 'Add ?mock=true to URL or set localStorage scheduledCalls_useMock to true'
          });
        }
        
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data: PendingScheduledCallsResponse = await response.json();
      Sentry.logger.info('API response data received', { 
        log_source: 'scheduled_call_service',
        organizations_count: data?.totalOrganizations,
        total_calls: data?.totalPendingCalls
      });
      
      // Cache the successful response
      responseCache.set(cacheKey, { data, timestamp: Date.now() });
      
      // Clean up old cache entries
      if (responseCache.size > 10) {
        const oldestKey = Array.from(responseCache.keys())[0];
        responseCache.delete(oldestKey);
      }
      
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        Sentry.logger.error('API request timed out', { 
          log_source: 'scheduled_call_service',
          timeout_seconds: 15
        });
        
        // If timeout in development, suggest mock data
        if (isDevelopment) {
          Sentry.logger.warn('Timeout in development, mock data available', { 
            log_source: 'scheduled_call_service',
            suggestion: 'Add ?mock=true to URL or set localStorage scheduledCalls_useMock to true'
          });
        }
        
        throw new Error('Request timed out after 15 seconds. Please try again or check your connection.');
      }
      Sentry.logger.error('API request failed', { 
        error,
        log_source: 'scheduled_call_service'
      });
      
      // If network error in development, suggest mock data
      if (isDevelopment && (error.message.includes('fetch') || error.message.includes('network'))) {
        Sentry.logger.warn('Network error in development', { 
          log_source: 'scheduled_call_service',
          error_message: error.message,
          suggestion: 'Add ?mock=true to URL'
        });
      }
      
      throw error;
    }
  },
};
